/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Clients.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
SELLERIE MARINE - PAGE GESTION CLIENTS

📋 DESCRIPTION:
Page React principale pour la gestion complète des clients.
Interface avec recherche, statistiques et intégration workflow devis.

🎯 RESPONSABILITÉS:
- Affichage liste complète des clients avec informations détaillées
- Recherche multi-critères (nom, contact, email, téléphone, adresse)
- Calcul automatique statistiques par client (nb devis, montant total)
- Actions CRUD complètes (voir, modifier, supprimer, créer devis)
- Intégration workflow création devis avec sélection client
- Mode spécial "retour devis" pour création client depuis configurateur
- Formatage automatique des montants et données contact

🔗 DÉPENDANCES:
- ClientService: CRUD clients et gestion données
- DevisApiService: Récupération devis pour calculs statistiques
- React Router: Navigation et gestion état entre pages
- useLocation/useNavigate: Gestion workflow devis intégré

📡 INTERACTIONS:
- Services: Chargement clients et devis pour statistiques
- Navigation: Workflow intégré création devis avec état partagé
- Interface: Recherche temps réel et actions contextuelles
- Configurateur: Mode retour depuis configurateur article
- Workflow: Transmission état articles devis entre pages

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Mode spécial "returnToDevis" pour workflow configurateur
- Calculs statistiques temps réel (nb devis, montant total)
- Interface responsive avec tableaux adaptatifs
- Actions contextuelles selon mode (normal vs workflow devis)
- Formatage localisé des montants (fr-FR)
- Recherche multi-critères instantanée
- Gestion état partagé pour workflow devis complet
*/
import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { clientService, Client } from '../services/ClientService';
import { devisApiService } from '../services/DevisApiService';
import { apiFetch } from '../services/httpClient';
import AuthContext from '../contexts/AuthContext';

// Types pour l'affichage des clients
interface ClientDisplay {
  id: string;
  nom: string;
  contact: string;
  email: string;
  telephone: string;
  adresse: string;
  nbDevis: number;
  montantTotal: number;
}

const Clients: React.FC = () => {
  const { access } = useContext(AuthContext);
  const canEdit = access?.access_mode === 'active' || access?.access_mode === 'trial';
  const navigate = useNavigate();
  const location = useLocation();
  
  // Vérifier si on est redirigé depuis le configurateur d'article
  const returnToDevis = location.state?.returnToDevis || false;
  const articlesDevis = location.state?.articlesDevis || [];
  
  const [clients, setClients] = useState<ClientDisplay[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [recherche, setRecherche] = useState<string>('');

  // Charger les clients depuis le service
  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        
        // Récupérer les clients depuis le service
        const clientsData = await clientService.getClients();
        
        // Récupérer les devis pour calculer les montants totaux
        const allDevis = await devisApiService.getDevis();
        
        // Transformer les données des clients pour l'affichage
        const clientsDisplay = clientsData.map(client => {
          // Filtrer les devis pour ce client
          const clientDevis = allDevis.filter(d => d.client === client.id);
          
          // Calculer le montant total des devis
          const montantTotal = clientDevis.reduce((total, devis) => total + (devis.montantTotal || 0), 0);
          
          return {
            id: client.id || '',
            nom: client.societe || `${client.prenom} ${client.nom}`,
            contact: `${client.prenom} ${client.nom}`,
            email: client.email,
            telephone: client.telephone,
            adresse: `${client.codePostal} ${client.ville}`,
            nbDevis: clientDevis.length,
            montantTotal: montantTotal
          };
        });
        
        setClients(clientsDisplay);
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des clients:', error);
        setLoading(false);
      }
    };
    
    fetchClients();
  }, []);

  // Filtrer les clients en fonction de la recherche
  const clientsFiltres = clients.filter((client) => {
    const searchLower = recherche.toLowerCase();
    return (
      client.nom.toLowerCase().includes(searchLower) ||
      client.contact.toLowerCase().includes(searchLower) ||
      client.email.toLowerCase().includes(searchLower) ||
      client.telephone.includes(recherche) ||
      client.adresse.toLowerCase().includes(searchLower)
    );
  });

  // Fonction pour formater les montants en euros
  const formatMontant = (montant: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(montant);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Gestion des clients</h1>
        <div className="flex space-x-2">
          {/* Bouton de test pour forcer l'alerte */}
          <button
            onClick={async () => {
              // Vider TOUS les caches pour forcer l'appel API
              localStorage.removeItem('clients');
              localStorage.removeItem('matieres');
              
              // Forcer l'appel direct a l'API qui va echouer
              try {
                setLoading(true);

                // Appel direct a l'API qui va echouer car backend arrete
                const data = await apiFetch<any>('/clients');

                setLoading(false);

              } catch (error) {

                console.error('Erreur API:', error);
                
                // Maintenant appeler le service qui va déclencher l'alerte
                try {
                  const clientsData = await clientService.getClients();

                } catch (serviceError) {
                  console.error('Erreur service:', serviceError);
                }
                
                setLoading(false);
              }
            }}
            className="bg-red-500 text-white px-3 py-2 rounded-md hover:bg-red-600 text-sm"
          >
            🚨 Test Alerte Clients
          </button>
          
          {returnToDevis ? (
            <button
              onClick={() => {
                if (!canEdit) {
                  alert("Action disponible uniquement en essai ou abonnement actif.");
                  return;
                }
                navigate('/clients/nouveau', { 
                  state: { 
                    returnToDevis: true,
                    articlesDevis: articlesDevis
                  } 
                });
              }}
              className={`btn-primary flex items-center ${canEdit ? '' : 'opacity-60 cursor-not-allowed'}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
              Créer un client pour le devis
            </button>
          ) : (
            <Link
              to="/clients/nouveau"
              className={`btn-primary flex items-center ${canEdit ? '' : 'opacity-60 cursor-not-allowed'}`}
              onClick={(e) => {
                if (!canEdit) {
                  e.preventDefault();
                  alert("Action disponible uniquement en essai ou abonnement actif.");
                }
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
              Nouveau client
            </Link>
          )}
        </div>
      </div>

      {/* Recherche */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            placeholder="Rechercher un client..."
            className="input-field w-full"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
          />
          <button
            className="bg-gray-100 p-2 rounded-md hover:bg-gray-200"
            onClick={() => setRecherche('')}
            title="Effacer la recherche"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-gray-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Liste des clients */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Client
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Contact
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Ville
              </th>
              <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                Devis
              </th>
              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Montant
              </th>
              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {clientsFiltres.length > 0 ? (
              clientsFiltres.map((client) => (
                <tr key={client.id} className="hover:bg-gray-50">
                  <td className="px-3 py-3 text-sm">
                    <div className="font-medium text-gray-900">{client.nom}</div>
                    <div className="text-gray-500 text-xs">{client.contact}</div>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-500">
                    <div className="truncate max-w-xs" title={client.email}>{client.email}</div>
                    <div className="text-xs">{client.telephone}</div>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-500">
                    {client.adresse}
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-500 text-center">
                    {client.nbDevis}
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-900 text-right font-medium">
                    {formatMontant(client.montantTotal)}
                  </td>
                  <td className="px-3 py-3 text-right text-sm">
                    <div className="flex justify-end space-x-3">
                        {returnToDevis ? (
                          <button 
                            onClick={() => navigate('/devis/nouveau', { 
                              state: { 
                                clientId: client.id,
                                articlesDevis: articlesDevis
                              } 
                            })} 
                            className="text-green-600 hover:text-green-900 font-bold"
                          >
                            Sélectionner pour le devis
                          </button>
                        ) : (
                          <>
                            <button 
                              onClick={() => navigate(`/clients/${client.id}`)} 
                              className="text-primary-600 hover:text-primary-900"
                            >
                              Voir
                            </button>
                            <button 
                              onClick={() => navigate(`/clients/${client.id}/edit`)} 
                              className="text-primary-600 hover:text-primary-900"
                            >
                              Modifier
                            </button>
                            <button 
                              onClick={() => navigate(`/devis/nouveau`, { state: { clientId: client.id } })} 
                              className="text-primary-600 hover:text-primary-900"
                            >
                              Créer devis
                            </button>
                            <button 
                              onClick={async () => {
                                if (window.confirm(`Êtes-vous sûr de vouloir supprimer le client "${client.nom}" ?\n\n⚠️ Attention : Tous les devis associés à ce client seront également supprimés.`)) {
                                  try {
                                    await clientService.deleteClient(client.id);
                                    // Recharger la liste des clients
                                    const updatedClients = clients.filter(c => c.id !== client.id);
                                    setClients(updatedClients);
                                    alert('Client supprimé avec succès');
                                  } catch (error) {
                                    console.error('Erreur lors de la suppression du client:', error);
                                    alert('Une erreur est survenue lors de la suppression du client');
                                  }
                                }
                              }} 
                              className="text-red-600 hover:text-red-900 font-medium"
                            >
                              Supprimer
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                    Aucun client ne correspond à vos critères de recherche.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
      </div>
    </div>
  );
};

export default Clients;
